import { useEffect, useRef, useState, type FormEvent } from "react";
import {
  ExtensionConnection,
  type ConnectionState,
  type ConnectionFocus,
} from "./AccountConnections";
type Api = <T>(
  path: string,
  options?: {
    method?: string;
    body?: unknown;
    csrf?: string | undefined;
    signal?: AbortSignal;
  },
) => Promise<T>;
type Settings = {
  deviceId: string | null;
  voicemailEnabled: boolean;
  doNotDisturb: boolean;
  callWaiting: boolean;
  ringSeconds: number;
  forwardMode: "off" | "always" | "busy" | "unanswered";
  forwardTo: string;
  updatedAt: string;
};
export type Extension = {
  id: string;
  digits: string;
  label: string;
  locationId: string;
  locationName: string;
  userName: string | null;
  version: number;
  applicationStatus: "not_applied";
  settings: Settings | null;
};
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export function decodeExtensions(value: unknown): Extension[] {
  const bad = () =>
    new Error("The extension records could not be read. Reload to try again.");
  if (!value || typeof value !== "object") throw bad();
  const data = value as Record<string, unknown>;
  if (
    data.phoneSystemConnected !== false ||
    data.scope !== "account" ||
    !Array.isArray(data.items) ||
    data.items.length > 1000
  )
    throw bad();
  const ids = new Set<string>();
  return data.items.map((item: unknown) => {
    if (!item || typeof item !== "object") throw bad();
    const r = item as Record<string, unknown>;
    if (
      typeof r.id !== "string" ||
      !uuid.test(r.id) ||
      ids.has(r.id) ||
      typeof r.digits !== "string" ||
      !/^\d{2,8}$/.test(r.digits) ||
      typeof r.label !== "string" ||
      !r.label ||
      r.label.length > 120 ||
      typeof r.locationId !== "string" ||
      !uuid.test(r.locationId) ||
      typeof r.locationName !== "string" ||
      (r.userName !== null && typeof r.userName !== "string") ||
      typeof r.version !== "number" ||
      !Number.isSafeInteger(r.version) ||
      r.version < 0 ||
      r.applicationStatus !== "not_applied"
    )
      throw bad();
    ids.add(r.id);
    let settings: Settings | null = null;
    if (r.settings !== null) {
      if (!r.settings || typeof r.settings !== "object" || r.version === 0)
        throw bad();
      const s = r.settings as Record<string, unknown>;
      if (
        (s.deviceId !== null &&
          (typeof s.deviceId !== "string" || !uuid.test(s.deviceId))) ||
        typeof s.voicemailEnabled !== "boolean" ||
        typeof s.doNotDisturb !== "boolean" ||
        typeof s.callWaiting !== "boolean" ||
        typeof s.ringSeconds !== "number" ||
        !Number.isInteger(s.ringSeconds) ||
        s.ringSeconds < 5 ||
        s.ringSeconds > 120 ||
        typeof s.forwardMode !== "string" ||
        !["off", "always", "busy", "unanswered"].includes(
          String(s.forwardMode),
        ) ||
        typeof s.forwardTo !== "string" ||
        (s.forwardMode === "off"
          ? s.forwardTo !== ""
          : !/^\+1[2-9]\d{2}[2-9]\d{6}$/.test(s.forwardTo)) ||
        typeof s.updatedAt !== "string" ||
        !Number.isFinite(Date.parse(s.updatedAt))
      )
        throw bad();
      settings = {
        deviceId: s.deviceId as string | null,
        voicemailEnabled: s.voicemailEnabled,
        doNotDisturb: s.doNotDisturb,
        callWaiting: s.callWaiting,
        ringSeconds: s.ringSeconds,
        forwardMode: s.forwardMode as Settings["forwardMode"],
        forwardTo: s.forwardTo,
        updatedAt: s.updatedAt,
      };
    } else if (r.version !== 0) throw bad();
    return {
      id: r.id,
      digits: r.digits,
      label: r.label,
      locationId: r.locationId,
      locationName: r.locationName,
      userName: r.userName as string | null,
      version: r.version,
      applicationStatus: "not_applied",
      settings,
    };
  });
}
type Device = {
  id: string;
  model: string;
  label?: string;
  location_id?: string | null;
};
type Props = {
  api: Api;
  accountId: string;
  customer: boolean;
  csrf?: string | undefined;
  readOnly?: boolean;
  locations: { id: string; name: string }[];
  devices: Device[];
  refreshVersion: number;
  onRecordings: () => void;
  onLocations: () => void;
  onFeatures?: () => void;
  onEquipment?: () => void;
  connectionState?: ConnectionState;
  onAssignment?: (focus: ConnectionFocus) => void;
  onChanged?: () => Promise<void>;
};

function Preferences({
  extension,
  devices,
  readOnly,
  saving,
  onSave,
}: {
  extension: Extension;
  devices: Device[];
  readOnly: boolean;
  saving: boolean;
  onSave: (body: Record<string, unknown>) => Promise<boolean>;
}) {
  const s = extension.settings;
  const [forward, setForward] = useState(s?.forwardMode ?? "off");
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (readOnly || saving) return;
    const data = new FormData(event.currentTarget);
    await onSave({
      extensionId: extension.id,
      expectedVersion: extension.version,
      label: String(data.get("label")),
      deviceId: s?.deviceId ?? null,
      voicemailEnabled: data.get("voicemail") === "on",
      doNotDisturb: data.get("dnd") === "on",
      callWaiting: data.get("waiting") === "on",
      ringSeconds: Number(data.get("ringSeconds")),
      forwardMode: forward,
      forwardTo:
        forward === "off" ? "" : String(data.get("forwardTo") ?? "").trim(),
    });
  };
  return (
    <form className="cr-form" onSubmit={submit}>
      <p>
        {s
          ? `Requested settings saved ${new Date(s.updatedAt).toLocaleString()}.`
          : "No settings saved. The choices below are proposed defaults, not readings from your phone."}{" "}
        Not applied to the phone system.
      </p>
      <fieldset className="cr-extension-fields" disabled={saving || readOnly}>
        <legend>Requested settings for extension {extension.digits}</legend>
        <label className="cr-field">
          <span>Extension name</span>
          <input
            name="label"
            required
            maxLength={120}
            defaultValue={extension.label}
          />
        </label>
        <p>
          Equipment:{" "}
          {s?.deviceId
            ? devices.find((device) => device.id === s.deviceId)?.label ||
              devices.find((device) => device.id === s.deviceId)?.model ||
              "Saved equipment needs review"
            : "Not assigned"}
          . Staff can change equipment and ports in Routing.
        </p>
        <label className="cr-check">
          <input
            type="checkbox"
            name="voicemail"
            defaultChecked={s?.voicemailEnabled ?? true}
          />
          Enable voicemail
        </label>
        <label className="cr-check">
          <input
            type="checkbox"
            name="dnd"
            defaultChecked={s?.doNotDisturb ?? false}
          />
          Do not disturb
        </label>
        <label className="cr-check">
          <input
            type="checkbox"
            name="waiting"
            defaultChecked={s?.callWaiting ?? true}
          />
          Call waiting
        </label>
        <label className="cr-field">
          <span>Ring time, seconds</span>
          <input
            name="ringSeconds"
            type="number"
            min={5}
            max={120}
            step={1}
            required
            defaultValue={s?.ringSeconds ?? 25}
          />
        </label>
        <label className="cr-field">
          <span>Call forwarding</span>
          <select
            value={forward}
            onChange={(e) =>
              setForward(e.target.value as Settings["forwardMode"])
            }
          >
            <option value="off">Off</option>
            <option value="always">All calls</option>
            <option value="busy">When busy</option>
            <option value="unanswered">When unanswered</option>
          </select>
        </label>
        {forward !== "off" && (
          <label className="cr-field">
            <span>Forward to a US or Canadian number</span>
            <input
              type="tel"
              name="forwardTo"
              required
              pattern="\+1[2-9][0-9]{2}[2-9][0-9]{6}"
              maxLength={12}
              placeholder="+1 and the ten-digit number"
              defaultValue={s?.forwardTo ?? ""}
            />
            <small>
              This request does not change live forwarding. Cache River must
              apply and verify it on the phone system.
            </small>
          </label>
        )}
        {!readOnly && (
          <button className="cr-button" disabled={saving}>
            {saving ? "Saving…" : "Save requested settings"}
          </button>
        )}
      </fieldset>
      {readOnly && (
        <p>Customer view is read-only. Exit it to change settings.</p>
      )}
    </form>
  );
}

export function ExtensionsPanel({
  api,
  accountId,
  customer,
  csrf,
  readOnly = false,
  locations,
  devices,
  refreshVersion,
  onRecordings,
  onLocations,
  onFeatures,
  onEquipment,
  connectionState,
  onAssignment,
  onChanged,
}: Props) {
  const [items, setItems] = useState<Extension[] | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [revision, setRevision] = useState(0);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const alive = useRef(false),
    busy = useRef(false);
  const command = useRef({ fingerprint: "", key: "" });
  const base = `/${customer ? "customer" : "admin"}/accounts/${accountId}/extensions`;
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  useEffect(() => {
    const abort = new AbortController();
    setLoading(true);
    setError("");
    void api<unknown>(base, { signal: abort.signal })
      .then(decodeExtensions)
      .then((result) => {
        if (!abort.signal.aborted) setItems(result);
      })
      .catch((cause) => {
        if (!abort.signal.aborted) {
          setError(cause.message);
          setItems(null);
        }
      })
      .finally(() => {
        if (!abort.signal.aborted) setLoading(false);
      });
    return () => abort.abort();
  }, [api, base, revision, refreshVersion]);
  async function save(body: Record<string, unknown>, settings = false) {
    if (busy.current || readOnly || loading) return false;
    busy.current = true;
    setSaving(true);
    setError("");
    setNotice("");
    const path = base + (settings ? "/settings" : "");
    const fingerprint = JSON.stringify({ path, body });
    if (command.current.fingerprint !== fingerprint)
      command.current = { fingerprint, key: crypto.randomUUID() };
    try {
      const result = await api<{ id: string; applicationStatus: string }>(
        path,
        {
          method: "POST",
          csrf,
          body: { ...body, requestKey: command.current.key },
        },
      );
      if (!uuid.test(result.id) || result.applicationStatus !== "not_applied")
        throw new Error(
          "The save result could not be confirmed. Retry with the same details.",
        );
      if (!alive.current) return false;
      setNotice(
        settings
          ? "Requested settings saved. They have not been applied to the phone system."
          : "Extension saved for setup. No additional public number was purchased and live calls are unchanged.",
      );
      setRevision((v) => v + 1);
      await onChanged?.();
      return true;
    } catch (cause) {
      if (alive.current) setError((cause as Error).message);
      return false;
    } finally {
      busy.current = false;
      if (alive.current) setSaving(false);
    }
  }
  async function prepareSoftphone(item: Extension) {
    if (busy.current || readOnly || customer || loading) return;
    busy.current = true;
    setSaving(true);
    setError("");
    setNotice("");
    const fingerprint = `softphone:${item.id}:${item.version}`;
    if (command.current.fingerprint !== fingerprint)
      command.current = { fingerprint, key: crypto.randomUUID() };
    try {
      const result = await api<{ prepared: boolean; deviceId: string }>(
        `${base}/${item.id}/softphone`,
        {
          method: "POST",
          csrf,
          body: {
            requestKey: command.current.key,
            expectedExtensionVersion: item.version,
            confirmed: true,
          },
        },
      );
      if (!result.prepared || !uuid.test(result.deviceId))
        throw new Error(
          "The assignment could not be confirmed. Reload before retrying.",
        );
      await onChanged?.();
      if (!alive.current) return;
      setRevision((v) => v + 1);
      setNotice(
        `Softphone assigned to ${item.digits}. Open Equipment, then Manual phone setup to install it and issue connection details. Calls are unchanged until installation.`,
      );
    } catch (cause) {
      if (alive.current) setError((cause as Error).message);
    } finally {
      busy.current = false;
      if (alive.current) setSaving(false);
    }
  }
  return (
    <section aria-label="Business extensions">
      <div className="cr-extension-heading">
        <div>
          <h3>Extensions</h3>
          <p>
            Give each person or shared phone an internal extension. Several
            extensions can share the business's public number.
          </p>
        </div>
        <button
          className="cr-button secondary"
          disabled={loading || saving}
          onClick={() => setRevision((v) => v + 1)}
        >
          Reload extensions
        </button>
      </div>
      <div className="cr-notice">
        <strong>Extension setup</strong>
        <p>
          Add people and shared phones here, then assign equipment in Routing.
          Calling Features shows saved settings and their live apply status.
          Open Voicemail for commissioned mailboxes. Adding an extension alone
          does not install a phone account or change incoming calls.
        </p>
      </div>
      {notice && (
        <p className="cr-notice" role="status">
          {notice}
        </p>
      )}
      {error && (
        <p className="cr-notice error" role="alert">
          {error}
        </p>
      )}
      {loading && <p role="status">Loading extensions…</p>}
      {items?.length === 0 && !loading && (
        <p>
          {readOnly
            ? "No extensions have been added. Exit customer view to add one as staff."
            : "No extensions have been added. Add a name and internal number below."}
        </p>
      )}
      {items?.map((item) => (
        <details key={item.id} className="cr-panel cr-editor cr-extension-row">
          <summary>
            <strong>
              {item.digits} · {item.label}
            </strong>
            <span>{item.locationName}</span>
          </summary>
          {item.userName && <p>Assigned user: {item.userName}</p>}
          {!customer &&
            !readOnly &&
            accountId === "f59f3eb9-55bd-4dc3-bb00-3361a3abf9a0" &&
            !item.settings?.deviceId &&
            /^6\d{3}$/.test(item.digits) &&
            Number(item.digits) >= 6001 &&
            item.digits !== "6200" && (
              <p>
                <button
                  className="cr-button secondary"
                  disabled={saving || loading}
                  onClick={() => void prepareSoftphone(item)}
                >
                  {saving ? "Working…" : "Assign a Tailscale softphone"}
                </button>
              </p>
            )}
          {connectionState && (
            <ExtensionConnection
              state={connectionState}
              extensionId={item.id}
              onEdit={!customer && !readOnly ? onAssignment : undefined}
            />
          )}
          {onFeatures ? (
            <button className="cr-button secondary" onClick={onFeatures}>
              Manage calling features and Caller ID
            </button>
          ) : (
            <Preferences
              key={`${item.id}:${item.version}`}
              extension={item}
              devices={devices}
              saving={saving || loading}
              readOnly={readOnly}
              onSave={(body) => save(body, true)}
            />
          )}
        </details>
      ))}
      {!readOnly && (
        <section className="cr-panel">
          <h3>Add an extension</h3>
          {locations.length ? (
            <form
              className="cr-form"
              onSubmit={async (e) => {
                e.preventDefault();
                const form = e.currentTarget,
                  data = new FormData(form);
                if (
                  await save({
                    digits: String(data.get("digits") ?? ""),
                    label: String(data.get("label") ?? ""),
                    locationId: String(data.get("locationId") ?? ""),
                  })
                )
                  form.reset();
              }}
            >
              <fieldset
                className="cr-extension-fields"
                disabled={saving || loading || items === null}
              >
                <legend>New internal extension</legend>
                <label className="cr-field">
                  <span>Extension number</span>
                  <input
                    name="digits"
                    inputMode="numeric"
                    pattern="[0-9]{2,8}"
                    minLength={2}
                    maxLength={8}
                    required
                  />
                  <small>
                    Use 2 to 8 digits, excluding emergency and service numbers.
                  </small>
                </label>
                <label className="cr-field">
                  <span>Name of the person or shared phone</span>
                  <input name="label" maxLength={120} required />
                </label>
                <label className="cr-field">
                  <span>Physical service location</span>
                  <select name="locationId" required defaultValue="">
                    <option value="" disabled>
                      Choose a location
                    </option>
                    {locations.map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.name}
                      </option>
                    ))}
                  </select>
                </label>
                <button className="cr-button" disabled={saving || loading}>
                  {saving ? "Saving…" : "Add extension for setup"}
                </button>
              </fieldset>
            </form>
          ) : (
            <>
              <p>
                A physical service location is needed before adding extensions.
                No emergency address has been assumed.
              </p>
              <button className="cr-button secondary" onClick={onLocations}>
                Open service locations
              </button>
            </>
          )}
        </section>
      )}
      {onEquipment && (
        <p>
          <button className="cr-button secondary" onClick={onEquipment}>
            Open Equipment and connection details
          </button>
        </p>
      )}
      <section className="cr-panel">
        <h3>Greetings</h3>
        <p>
          Create and download greetings in Recordings. To record a live mailbox
          greeting from a commissioned phone, dial *97 and follow the mailbox
          options. Downloading a recording does not install it automatically.
        </p>
        <button className="cr-button secondary" onClick={onRecordings}>
          Open Recordings
        </button>
      </section>
    </section>
  );
}
